/**
 * What the Smart Pen must never touch (SRS C5).
 *
 * The recogniser answers "is this a circle?" honestly, and a written `0` honestly is
 * one. Nothing about the shape of a single stroke can separate the two — so nothing
 * here tries. It asks a different question: was the hand writing?
 *
 * A written character almost never arrives alone. It sits on a line with others of
 * its own size, moments apart, and that company is what gives it away. A shape does
 * not: the moment one snaps it stops being ink, so it is no longer company for the
 * next one, and a row of circles snaps one after another exactly as it should.
 *
 * The other two rules need no cleverness. A mark too small to be a shape is not one,
 * and the highlighter marks work rather than draws it.
 */

import { MIN_SNAPPABLE_LENGTH_CANVAS_PX } from '@opal/contracts';
import type { Rect, Stroke } from '@opal/shared';

/** How long a finished stroke goes on counting as company for the one beside it. */
export const WRITING_MEMORY_MS = 4000;

/** Neighbours more than this many times bigger or smaller are not the same writing. */
const NEIGHBOUR_SIZE_RATIO = 3;

/**
 * How far away other ink still counts as company, as a share of its size.
 *
 * Reaches above and below as well as sideways, because the first letter of a new
 * line has nothing beside it yet — and a teacher starting the next line of a worked
 * example is as plainly writing as they were a second ago on the line above.
 */
const NEIGHBOUR_REACH_RATIO = 2;

/** One stroke the board is still holding as ink, and when the hand left it. */
export interface RecentInk {
  readonly id: string;
  readonly at: number;
  readonly bounds: Rect;
}

/** How big a mark is, whichever way round it was drawn. */
function sizeOf(box: Rect): number {
  return Math.max(box.width, box.height);
}

/**
 * SRS C5 — a mark this small is a dot, a tick or a comma, and never a shape.
 *
 * Measured across its longest side, which is what "shorter than 20 canvas px" means
 * to the person who wrote it down: how big the thing on the board is.
 */
export function tooSmallToSnap(bounds: Rect): boolean {
  return sizeOf(bounds) < MIN_SNAPPABLE_LENGTH_CANVAS_PX;
}

/** SRS C4 — the highlighter marks what is already there. It never becomes a shape. */
export function drawnWithHighlighter(stroke: Stroke): boolean {
  return stroke.tool === 'highlighter';
}

/**
 * Whether one mark is close company for another.
 *
 * Size decides almost everything here. Two marks of the same size a moment apart are
 * writing; a rough square is several times the height of the words beside it, so it
 * is not company for them and they are not company for it. Measured on the longer
 * side of each, because an equals sign is two flat bars — barely any height at all,
 * and no relation to the digits either side of it unless you measure what a reader
 * actually sees.
 */
function closeCompany(a: Rect, b: Rect): boolean {
  const bigger = Math.max(sizeOf(a), sizeOf(b));
  const smaller = Math.min(sizeOf(a), sizeOf(b));
  if (smaller <= 0 || bigger > smaller * NEIGHBOUR_SIZE_RATIO) return false;

  const reach = bigger * NEIGHBOUR_REACH_RATIO;
  const across = Math.max(a.x - (b.x + b.width), b.x - (a.x + a.width), 0);
  const down = Math.max(a.y - (b.y + b.height), b.y - (a.y + a.height), 0);
  return across <= reach && down <= reach;
}

/**
 * Whether this mark was made while writing rather than while drawing.
 *
 * True as soon as one piece of ink of its own size is sitting beside it from the
 * last few seconds. One is enough: a wrongly snapped word is a worse thing to do to
 * a teacher mid-sentence than a shape that failed to tidy itself up.
 */
export function looksLikeWriting(
  bounds: Rect,
  at: number,
  recent: readonly RecentInk[],
): boolean {
  return recent.some(
    (ink) => Math.abs(ink.at - at) <= WRITING_MEMORY_MS && closeCompany(bounds, ink.bounds),
  );
}
